import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "es" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
}

const translations = {
  en: {
    // Navbar
    "home": "Home",
    "about": "About",
    "solutions": "Solutions",
    "case-studies": "Case Studies",
    "blog": "Blog",
    "contact": "Contact",
    "request_demo": "Request Demo",
    "primary_cta": "Start the conversation",

    // Hero Section
    "hero_title": "Private AI frameworks for sensitive enterprise workflows",
    "hero_subtitle": "For organizations that need secure automation, governed intelligence, and control over sensitive data.",
    "hero_description": "Inbloqs helps enterprises design and deploy private AI frameworks, agent methodologies, and automation systems inside their own infrastructure so teams can accelerate analysis, decision support, and operations without losing control of sensitive information.",
    "hero_badge": "Private AI frameworks • Agent methodologies • Enterprise governance",
    "hero_future": "for the Future",
    "deploy_ai": "Explore private AI frameworks",
    "explore_automation": "Explore automation methodologies",
    "assess_readiness": "Assess organizational readiness",
    "build_rag_pilot": "Design a governed knowledge workflow",
    "hero_segments_intro": "Built for teams in:",
    "hero_segment_public": "Government & Public Sector",
    "hero_segment_manufacturing": "Manufacturing & Industrial Ops",
    "hero_segment_compliance": "Legal, Compliance & Regulated Enterprises",
    "hero_segment_m365": "Knowledge-Intensive Organizations",
    "hero_segment_rag": "Private Knowledge & Document Workflows",
    "join_cursos": "Join Cursos learning platform",

    // About Section
    "about_title": "Our Vision",
    "company_desc": "We help organizations deploy private AI frameworks, governed automation, and hybrid enterprise platforms that keep sensitive data inside approved infrastructure while improving clarity, control, and operational efficiency.",
    "our_mission": "Our Mission",
    "mission_statement": "At Inbloqs, we're committed to making the next generation of AI practical for enterprises of all sizes. We believe modern AI should be:",
    "private_secure": "Private & Secure",
    "private_secure_desc": "Your data stays within your infrastructure, never leaving your control.",
    "powerful_accessible": "Powerful & Accessible",
    "powerful_accessible_desc": "Enterprise-grade AI capabilities without requiring specialized expertise.",
    "integrated_automated": "Integrated & Automated",
    "integrated_automated_desc": "Seamlessly connected to your workflows, cloud infrastructure, and business platforms.",
    "tech_stack": "Our Technology Stack",
    "ai_models_label": "Supported Models:",
    "ai_models_list": "• Private and hybrid deployment patterns\n• Governed inference and retrieval workflows\n• Enterprise-approved model selection based on policy and risk\n• Agent orchestration patterns for sensitive operations\n• Fine-tuned, hybrid-ready delivery methodologies",
    "local_ai": "Local AI Hosting",
    "local_ai_list": "• Koboldcpp\n• OpenWebUI\n• Docker & Kubernetes\n• On-premise deployment",
    "automation_stack": "Automation",
    "automation_list": "• Workflows\n• AI-guided decision support\n• Enterprise productivity integration\n• Data pipelines",
    "security_stack": "Security",
    "security_list": "• End-to-end encryption\n• Privacy-first architecture\n• Secure API integration\n• Role-based access control",

    // About Features
    "digital_sovereignty": "Digital Sovereignty",
    "digital_sovereignty_desc": "Ensuring complete control over your AI processes and data with on-premise solutions that maintain independence and security.",

    "regulated_sectors": "Ideal Customer Profiles",
    "regulated_sectors_desc": "Focused on government, manufacturing, legal, compliance, and knowledge-intensive organizations that need private AI for sensitive workflows.",

    "open_innovation": "Open Innovation",
    "open_innovation_desc": "Promoting open-source solutions and collaborative development to drive innovation and transparency in AI implementation.",

    "democratizing_ai": "Democratizing AI",
    "democratizing_ai_desc": "Making advanced AI capabilities accessible to organizations of all sizes through scalable and cost-effective solutions.",

    // Solutions Section
    "solutions_title": "Private AI Services for Sensitive Workflows",
    "solutions_subtitle": "Services designed for regulated, document-heavy, and governed environments where methodology, privacy, and operational outcomes matter.",
    "onprem_ai_title": "Private AI & Document Intelligence",
    "onprem_ai_desc": "Design private AI systems for document analysis, knowledge retrieval, and sensitive workflows inside approved infrastructure and governance boundaries.",
    "automation_title": "Agentic Automation Methodologies",
    "automation_desc": "Structure document-heavy and cross-team processes with agent frameworks, orchestration methods, and governed automation patterns.",
    "training_title": "Training & Upskilling",
    "training_desc": "Equip teams to evaluate, adopt, and operate private AI frameworks, governance practices, and agent methodologies.",
    "infrastructure_title": "Secure Platforms & Hybrid AI",
    "infrastructure_desc": "Build the platform, governance, security, and identity foundations required to scale private AI safely.",

    // Features
    "feature_private_ai": "Private deployment frameworks with configurable model selection and governance-aligned operating patterns",
    "feature_automation": "Agentic workflow design with orchestration patterns, delegation methods, and human oversight",
    "feature_secure_chatbots": "Secure, self-hosted AI chatbots & assistants",
    "feature_rag": "Governed knowledge retrieval workflows across internal systems and business platforms",
    "feature_doc_analysis": "Enterprise document, email, and workspace analysis for compliance, operations, and technical knowledge",
    "feature_ai_workflows": "AI-enhanced automation workflows for departments and business units",
    "feature_microsoft": "Secure integration with enterprise productivity, knowledge, and document workflow systems",
    "feature_pipelines": "Automated data pipelines connecting AI models",
    "feature_custom_workflows": "Custom workflow development & integration",
    "feature_masterclasses": "Masterclasses on private AI, knowledge workflows, and model adaptation",
    "feature_hands_on": "Hands-on training with various AI technologies",
    "feature_certification": "Enterprise AI certification programs",
    "feature_learning": "Customized learning paths for organizations",
    "feature_hosting": "On-prem, cloud, and hybrid AI hosting solutions",
    "feature_docker": "Docker & Kubernetes orchestration for scaling",
    "feature_encryption": "End-to-end data encryption & security protocols",
    "feature_architecture": "Privacy-first architecture design & implementation",

    // Real-time AI Models section
    "realtime_ai_models": "Real-time AI Models",
    "realtime_ai_models_desc": "See our on-premise AI solutions in action",

    // Case Studies
    "case_studies_title": "Practical Use Cases",
    "case_studies_subtitle": "Representative pilot results for private AI, governed automation, and knowledge workflow deployments.",
    "case_study_1_title": "Knowledge Ops Assistant",
    "case_study_1_subtitle": "Private Knowledge Operations",
    "case_study_1_desc": "A secure assistant for document search, drafting, and workflow support that keeps sensitive enterprise content inside approved security boundaries.",
    "case_study_2_title": "Compliance Operations Hub",
    "case_study_2_subtitle": "Hybrid Service Integration",
    "case_study_2_desc": "A governed workflow for compliance review, evidence collection, and policy tasks that accelerates audit preparation without weakening control.",
    "case_study_3_title": "Agentic Service Desk",
    "case_study_3_subtitle": "Agent Orchestration Methodology",
    "case_study_3_desc": "An agent-orchestrated service workflow that triages, summarizes, and routes requests with clear escalation paths and human oversight.",
    "case_study_4_title": "Regulated Data Analysis Pipeline",
    "case_study_4_subtitle": "Private Cloud & Secure Analytics",
    "case_study_4_desc": "A governed analytics pipeline for regulated data and document sets, built to scale across private and cloud-connected environments.",
    "representative_pilot": "Representative pilot result",
    "case_studies_note": "Illustrative pilot benchmarks from scoped engagements. We can share methodology notes, workflow maps, architecture diagrams, and anonymized walkthroughs during qualification.",
    "efficiency": "Processing Speed",
    "adoption": "Data Coverage",
    "time_saved": "Time Reduction",
    "cost_savings": "Cost Reduction",
    "capacity": "User Capacity",
    "hours_week": "Weekly Hours Saved",
    "accuracy": "Detection Accuracy",
    "daily_users": "Daily Users",
    "response_time": "Response Time",
    "completion": "Task Completion",
    "staff_trained": "Staff Trained",
    "training_time": "Implementation Time",

    // Blog
    "blog_title": "Blog & Insights Hub",
    "blog_subtitle": "Insights on agent frameworks, hybrid AI platforms, and enterprise deployment patterns",
    "read_more": "Read more",
    "view_all_posts": "View all blog posts",

    // Contact
    "contact_title": "Plan Your Private AI Roadmap",
    "contact_subtitle": "Start a conversation about private AI strategy, governed automation, and the frameworks needed to move from exploration to production.",
    "contact_info": "Contact Information",
    "email_us": "Email Us",
    "call_us": "Call Us",
    "location": "Location",
    "join_community": "Join our community",
    "newsletter_desc": "Subscribe for updates on agent frameworks, hybrid cloud AI, and enterprise deployment patterns.",
    "subscribe": "Subscribe",
    "your_email": "Your email address",
    "request_demo_form": "Start a Private AI Conversation",
    "full_name": "Full Name",
    "email_address": "Email Address",
    "company": "Company",
    "your_company": "Your company name",
    "interest_area": "Interest Area",
    "message": "Message",
    "message_placeholder": "Tell us about your data sensitivity, workflow goals, governance requirements, and the systems your teams depend on...",
    "form_success": "Thank you! Your message has been sent successfully.",
    "form_error": "There was an error submitting your form. Please try again.",

    // Consulting
    "training_section_title": "Consulting, Best Practices & Strategy",
    "training_section_subtitle": "Advisory engagements for teams designing frontier AI operating models, governed automation, and hybrid enterprise deployment roadmaps",
    "training_program_1": {
      "title": "AI Strategy & Operating Model Design",
      "level": "Executive",
      "duration": "2-4 weeks",
      "format": "Advisory Sprint",
      "modules": [
        "Define enterprise AI north star and priorities",
        "Map value pools and high-impact workflows",
        "Design ownership model across IT, Security, and Business",
        "Build a phased roadmap from pilot to scaled production"
      ]
    },
    "training_program_2": {
      "title": "Platform, Governance & Best Practices",
      "level": "Enterprise",
      "duration": "2 weeks",
      "format": "Architecture Review",
      "modules": [
        "Reference architectures for hybrid AI on existing cloud infrastructure",
        "Security guardrails and policy enforcement patterns",
        "Evaluation, tracing, and quality governance",
        "Operational playbooks for reliability and change management"
      ]
    },
    "training_program_3": {
      "title": "Agentic Workflow & Orchestration Strategy",
      "level": "Advanced",
      "duration": "3-6 weeks",
      "format": "Guided Design Program",
      "modules": [
        "Design decision loops with human oversight",
        "Select orchestration patterns for reliability and control",
        "Prioritize automations by risk and business impact",
        "Blueprint reusable agent capabilities across business units"
      ]
    },
    "training_program_4": {
      "title": "Executive AI Advisory & Transformation",
      "level": "Leadership",
      "duration": "Ongoing",
      "format": "Advisory Retainer",
      "modules": [
        "Board-ready AI strategy narratives and KPIs",
        "Investment prioritization and capability sequencing",
        "Vendor and platform decision frameworks",
        "Quarterly strategic reviews and governance checkpoints"
      ]
    },
    "training_duration": "Duration",
    "training_format": "Format",
    "training_modules": "Modules",
    "training_details": "Scope Engagement",
    "training_register": "Discuss engagement",
    "interest_option_private_ai": "Private AI Frameworks",
    "interest_option_automation": "Automation Methodologies",
    "interest_option_consulting": "Readiness, Strategy & Architecture",
    "interest_option_platform": "Knowledge Workflows & Governance",
    "interest_option_other": "Other",

    // Footer
    "about_us": "About Us",
    "on_premise_ai": "On-Premise AI",
    "enterprise_automation": "Enterprise Automation",
    "capacitaciones": "Consulting & Strategy",
    "it_infrastructure": "IT Infrastructure",
    "resources": "Resources",
    "whitepapers": "Whitepapers",
    "case_studies": "Case Studies",
    "documentation": "Documentation",
    "legal": "Legal",
    "privacy_policy": "Privacy Policy",
    "terms_of_service": "Terms of Service",
    "cookie_policy": "Cookie Policy",
    "gdpr_compliance": "GDPR Compliance",
    "rights_reserved": "All rights reserved",
    "compliance_security": "Compliance & Security",
    "compliance_security_desc": "Enterprise-grade security and compliance frameworks for AI deployments.",
    "learn_more": "Learn More",
    "sending": "Sending...",
    "subscribe_success": "Thank you for subscribing to our newsletter!",
    "ai_deployment_info": "AI Deployment Info",
    "ai_deployment_desc": "Contact us to discuss how private AI frameworks can fit your organization.",
    "enterprise_automation_toast": "Enterprise Automation",
    "enterprise_automation_desc": "Discover how governed automation methodologies can support complex business processes.",
    "well_be_in_touch": "We'll be in touch soon!",
    success_stories: {
      title: "Practical Use Cases",
      subtitle: "Practical demonstrations of our AI solutions",
      stories: [
        {
          title: "Local AI Integration",
          description: "Demonstration of local AI solutions for secure and private data processing",
          metrics: {
            efficiency: "90%",
            accuracy: "95%",
            users: "500+",
            time: "3 months"
          }
        },
        {
          title: "Microsoft 365 Integration",
          description: "Demonstration of local AI integration with Microsoft 365 for technical documentation and compliance",
          metrics: {
            efficiency: "85%",
            accuracy: "92%",
            users: "1000+",
            time: "4 months"
          }
        },
        {
          title: "AI-Powered Automation",
          description: "Demonstration of AI-powered automation for enterprise processes",
          metrics: {
            efficiency: "95%",
            accuracy: "98%",
            users: "2000+",
            time: "6 months"
          }
        }
      ]
    },
    metrics: {
      efficiency: "Processing Speed",
      capacity: "User Capacity",
      time_saved: "Time Reduction",
      accuracy: "Detection Accuracy",
      response_time: "Response Time",
      hours_week: "Weekly Hours Saved",
      efficiency_improvement: "Efficiency Improvement",
      process_automation_rate: "Process Automation Rate",
      error_reduction: "Error Reduction",
      document_accuracy: "Document Accuracy",
      processing_speed: "Processing Speed",
      cost_savings: "Cost Savings",
      response_accuracy: "Response Accuracy",
      customer_satisfaction: "Customer Satisfaction",
      deployment_time: "Deployment Time",
      result_accuracy: "Result Accuracy",
      data_processing_efficiency: "Data Processing Efficiency"
    }
  },

  es: {
    // Navbar
    "home": "Inicio",
    "about": "Nosotros",
    "solutions": "Soluciones",
    "case-studies": "Casos de Uso Práctico",
    "blog": "Blog",
    "contact": "Contacto",
    "request_demo": "Solicitar Demo",
    "primary_cta": "Iniciar la conversación",

    // Hero Section
    "hero_title": "Frameworks de IA privada para flujos empresariales sensibles",
    "hero_subtitle": "Para organizaciones que necesitan automatización segura, inteligencia gobernada y control sobre datos sensibles.",
    "hero_description": "Inbloqs ayuda a las empresas a diseñar y desplegar frameworks de IA privada, metodologías agentivas y sistemas de automatización dentro de su propia infraestructura para acelerar análisis, apoyo a decisiones y operaciones sin perder el control de la información sensible.",
    "hero_badge": "Frameworks de IA privada • Metodologías agentivas • Gobernanza empresarial",
    "hero_future": "para el Futuro",
    "deploy_ai": "Explorar frameworks de IA privada",
    "explore_automation": "Explorar metodologías de automatización",
    "assess_readiness": "Evaluar preparación organizacional",
    "build_rag_pilot": "Diseñar un flujo de conocimiento gobernado",
    "hero_segments_intro": "Diseñado para equipos en:",
    "hero_segment_public": "Gobierno y sector público",
    "hero_segment_manufacturing": "Manufactura y operaciones industriales",
    "hero_segment_compliance": "Legal, cumplimiento y empresas reguladas",
    "hero_segment_m365": "Organizaciones intensivas en conocimiento",
    "hero_segment_rag": "Flujos privados de conocimiento y documentos",
    "join_cursos": "Únase a la plataforma Cursos",

    // About Section
    "about_title": "Nuestra Visión",
    "company_desc": "Ayudamos a las organizaciones a desplegar frameworks de IA privada, automatización gobernada y plataformas empresariales híbridas que mantienen los datos sensibles dentro de infraestructura aprobada mientras mejoran claridad, control y eficiencia operativa.",
    "our_mission": "Nuestra Misión",
    "mission_statement": "En Inbloqs, estamos comprometidos a hacer práctica la nueva generación de IA para empresas de todos los tamaños. Creemos que la IA moderna debe ser:",
    "private_secure": "Privada y Segura",
    "private_secure_desc": "Sus datos permanecen dentro de su infraestructura, nunca salen de su control.",
    "powerful_accessible": "Potente y Accesible",
    "powerful_accessible_desc": "Capacidades de IA de nivel empresarial sin requerir conocimientos especializados.",
    "integrated_automated": "Integrada y Automatizada",
    "integrated_automated_desc": "Conectada perfectamente con sus flujos de trabajo, infraestructura cloud y plataformas comerciales.",
    "tech_stack": "Nuestra Pila Tecnológica",
    "ai_models_label": "Modelos Soportados:",
    "ai_models_list": "• Patrones de despliegue privado e híbrido\n• Flujos gobernados de inferencia y recuperación\n• Selección de modelos aprobada por política y riesgo\n• Patrones de orquestación para operaciones sensibles\n• Metodologías de entrega afinadas y listas para entornos híbridos",
    "local_ai": "Hosting de IA Local",
    "local_ai_list": "• Koboldcpp\n• OpenWebUI\n• Docker y Kubernetes\n• Implementación local",
    "automation_stack": "Automatización",
    "automation_list": "• Flujos de trabajo\n• Apoyo a decisiones guiado por IA\n• Integración con plataformas de productividad empresarial\n• Canales de datos",
    "security_stack": "Seguridad",
    "security_list": "• Encriptación de extremo a extremo\n• Arquitectura centrada en la privacidad\n• Integración API segura\n• Control de acceso basado en roles",

    // About Features
    "digital_sovereignty": "Soberanía Digital",
    "digital_sovereignty_desc": "Garantizando control total sobre tus procesos de IA y datos con soluciones on-premise que mantienen la independencia y seguridad.",

    "regulated_sectors": "Perfiles ideales de cliente",
    "regulated_sectors_desc": "Enfocado en gobierno, manufactura, legal, cumplimiento y organizaciones intensivas en conocimiento que necesitan IA privada para flujos sensibles.",

    "open_innovation": "Innovación Abierta",
    "open_innovation_desc": "Promoviendo soluciones de código abierto y desarrollo colaborativo para impulsar la innovación y transparencia en la implementación de IA.",

    "democratizing_ai": "Democratizando la IA",
    "democratizing_ai_desc": "Haciendo las capacidades avanzadas de IA accesibles a organizaciones de todos los tamaños mediante soluciones escalables y rentables.",

    // Solutions Section
    "solutions_title": "Servicios de IA privada para flujos sensibles",
    "solutions_subtitle": "Servicios diseñados para entornos regulados, intensivos en documentos y gobernados donde la metodología, la privacidad y los resultados operativos son críticos.",
    "onprem_ai_title": "IA privada e inteligencia documental",
    "onprem_ai_desc": "Diseñe sistemas de IA privada para análisis documental, recuperación de conocimiento y flujos sensibles dentro de infraestructura aprobada y límites de gobernanza.",
    "automation_title": "Metodologías de automatización agentiva",
    "automation_desc": "Estructure procesos intensivos en documentos y entre equipos con frameworks de agentes, métodos de orquestación y patrones de automatización gobernada.",
    "training_title": "Formación y Capacitación",
    "training_desc": "Equipe a sus equipos para evaluar, adoptar y operar frameworks de IA privada, prácticas de gobernanza y metodologías agentivas.",
    "infrastructure_title": "Plataformas Seguras e IA Híbrida",
    "infrastructure_desc": "Construya la plataforma, gobernanza, seguridad e identidad necesarias para escalar IA privada de forma segura.",

    // Features
    "feature_private_ai": "Frameworks de despliegue privado con selección configurable de modelos y patrones operativos alineados a gobernanza",
    "feature_automation": "Diseño de flujos agentivos con patrones de orquestación, métodos de delegación y supervisión humana",
    "feature_secure_chatbots": "Chatbots y asistentes de IA seguros y autogestionados",
    "feature_rag": "Flujos gobernados de recuperación de conocimiento a través de sistemas internos y plataformas empresariales",
    "feature_doc_analysis": "Análisis empresarial de documentos, correos y espacios de trabajo para cumplimiento, operaciones y conocimiento técnico",
    "feature_ai_workflows": "Flujos de trabajo de automatización mejorados con IA para áreas y unidades de negocio",
    "feature_microsoft": "Integración segura con sistemas empresariales de productividad, conocimiento y flujos documentales",
    "feature_pipelines": "Conductos de datos automatizados que conectan modelos de IA",
    "feature_custom_workflows": "Desarrollo e integración de flujos de trabajo personalizados",
    "feature_masterclasses": "Clases magistrales sobre IA privada, flujos de conocimiento y adaptación de modelos",
    "feature_hands_on": "Capacitación práctica con diversas tecnologías de IA",
    "feature_certification": "Programas de certificación de IA empresarial",
    "feature_learning": "Rutas de aprendizaje personalizadas para organizaciones",
    "feature_hosting": "Soluciones de alojamiento de IA en local, nube y entornos híbridos",
    "feature_docker": "Orquestación Docker y Kubernetes para escalar",
    "feature_encryption": "Protocolos de cifrado y seguridad de extremo a extremo",
    "feature_architecture": "Diseño e implementación de arquitectura centrada en la privacidad",

    // Real-time AI Models section
    "realtime_ai_models": "Modelos de IA en Tiempo Real",
    "realtime_ai_models_desc": "Vea nuestras soluciones de IA locales en acción",

    // Case Studies
    "case_studies_title": "Casos de Uso Práctico",
    "case_studies_subtitle": "Resultados representativos de pilotos para IA privada, automatización gobernada y despliegues de flujos de conocimiento.",
    "case_study_1_title": "Asistente de Operaciones de Conocimiento",
    "case_study_1_subtitle": "Operaciones privadas de conocimiento",
    "case_study_1_desc": "Un asistente seguro para búsqueda documental, redacción y apoyo de flujos que mantiene contenido empresarial sensible dentro de límites aprobados de seguridad.",
    "case_study_2_title": "Centro de Operaciones de Cumplimiento",
    "case_study_2_subtitle": "Integración híbrida de servicios",
    "case_study_2_desc": "Un flujo gobernado para revisión de cumplimiento, recopilación de evidencia y tareas de políticas que acelera la preparación de auditorías sin debilitar el control.",
    "case_study_3_title": "Mesa de Servicio Agentiva",
    "case_study_3_subtitle": "Metodología de orquestación agentiva",
    "case_study_3_desc": "Un flujo de servicio orquestado por agentes que clasifica, resume y enruta solicitudes con rutas claras de escalamiento y supervisión humana.",
    "case_study_4_title": "Pipeline de Análisis de Datos Regulados",
    "case_study_4_subtitle": "Nube Privada y Analítica Segura",
    "case_study_4_desc": "Un pipeline de análisis gobernado para datos y documentos regulados, diseñado para escalar en entornos privados y conectados a la nube.",
    "representative_pilot": "Resultado representativo de piloto",
    "case_studies_note": "Benchmarks ilustrativos de pilotos acotados. Podemos compartir notas metodológicas, mapas de flujo, diagramas de arquitectura y recorridos anonimizados durante la evaluación.",
    "efficiency": "Velocidad de Proceso",
    "adoption": "Cobertura de Datos",
    "time_saved": "Reducción de Tiempo",
    "cost_savings": "Reducción de Costos",
    "capacity": "Capacidad de Usuarios",
    "hours_week": "Horas Semanales Ahorradas",
    "accuracy": "Precisión de Detección",
    "daily_users": "Usuarios Diarios",
    "response_time": "Tiempo de Respuesta",
    "completion": "Completitud de Tareas",
    "staff_trained": "Personal Capacitado",
    "training_time": "Tiempo de Implementación",

    // Blog
    "blog_title": "Blog y Centro de Conocimiento",
    "blog_subtitle": "Perspectivas sobre frameworks de agentes, plataformas híbridas de IA y patrones de despliegue empresarial",
    "read_more": "Leer más",
    "view_all_posts": "Ver todas las entradas del blog",

    // Contact
    "contact_title": "Planifique su hoja de ruta de IA privada",
    "contact_subtitle": "Inicie una conversación sobre estrategia de IA privada, automatización gobernada y los frameworks necesarios para pasar de exploración a producción.",
    "contact_info": "Información de Contacto",
    "email_us": "Envíenos un Correo",
    "call_us": "Llámenos",
    "location": "Ubicación",
    "join_community": "Únase a nuestra comunidad",
    "newsletter_desc": "Suscríbase para recibir actualizaciones sobre frameworks de agentes, IA híbrida y patrones de despliegue empresarial.",
    "subscribe": "Suscribirse",
    "your_email": "Su dirección de correo electrónico",
    "request_demo_form": "Iniciar una conversación sobre IA privada",
    "full_name": "Nombre Completo",
    "email_address": "Dirección de Correo",
    "company": "Empresa",
    "your_company": "Nombre de su empresa",
    "interest_area": "Área de Interés",
    "message": "Mensaje",
    "message_placeholder": "Cuéntenos sobre la sensibilidad de sus datos, los objetivos del flujo, los requisitos de gobernanza y los sistemas de los que dependen sus equipos...",
    "form_success": "¡Gracias! Su mensaje ha sido enviado exitosamente.",
    "form_error": "Hubo un error al enviar su formulario. Por favor, inténtelo de nuevo.",

    // Consulting
    "training_section_title": "Consultoría, Mejores Prácticas y Diseño Estratégico",
    "training_section_subtitle": "Servicios de asesoría para equipos que diseñan modelos operativos de IA de frontera, automatización gobernada y hojas de ruta híbridas empresariales",
    "training_program_1": {
      "title": "Estrategia de IA y Diseño del Modelo Operativo",
      "level": "Ejecutivo",
      "duration": "2-4 semanas",
      "format": "Sprint de Asesoría",
      "modules": [
        "Definir visión y prioridades estratégicas de IA",
        "Mapear flujos de alto impacto y creación de valor",
        "Diseñar modelo de responsabilidad entre TI, Seguridad y Negocio",
        "Construir hoja de ruta de piloto a producción escalada"
      ]
    },
    "training_program_2": {
      "title": "Plataforma, Gobierno y Mejores Prácticas",
      "level": "Empresarial",
      "duration": "2 semanas",
      "format": "Revisión de Arquitectura",
      "modules": [
        "Arquitecturas de referencia para IA híbrida sobre infraestructura cloud existente",
        "Guardrails de seguridad y patrones de cumplimiento",
        "Evaluación continua, trazabilidad y calidad",
        "Playbooks operativos para confiabilidad y evolución"
      ]
    },
    "training_program_3": {
      "title": "Estrategia de Orquestación y Flujos Agentivos",
      "level": "Avanzado",
      "duration": "3-6 semanas",
      "format": "Programa Guiado de Diseño",
      "modules": [
        "Diseño de bucles de decisión con supervisión humana",
        "Selección de patrones de orquestación por riesgo y control",
        "Priorización de automatizaciones por impacto de negocio",
        "Diseño de capacidades de agentes reutilizables"
      ]
    },
    "training_program_4": {
      "title": "Advisory Ejecutivo para Transformación con IA",
      "level": "Liderazgo",
      "duration": "Continuo",
      "format": "Acompañamiento Estratégico",
      "modules": [
        "Narrativa estratégica y KPIs para dirección",
        "Priorización de inversiones y secuenciación de capacidades",
        "Framework de decisión de plataformas y proveedores",
        "Revisiones trimestrales de estrategia y gobierno"
      ]
    },
    "training_duration": "Duración",
    "training_format": "Formato",
    "training_modules": "Módulos",
    "training_details": "Definir Alcance",
    "training_register": "Conversar sobre el servicio",
    "interest_option_private_ai": "Frameworks de IA privada",
    "interest_option_automation": "Metodologías de automatización",
    "interest_option_consulting": "Preparación, estrategia y arquitectura",
    "interest_option_platform": "Flujos de conocimiento y gobernanza",
    "interest_option_other": "Otro",

    // Footer
    "about_us": "Sobre Nosotros",
    "on_premise_ai": "IA Local",
    "enterprise_automation": "Automatización Empresarial",
    "capacitaciones": "Consultoría y Estrategia",
    "it_infrastructure": "Infraestructura TI",
    "resources": "Recursos",
    "whitepapers": "Documentos Técnicos",
    "case_studies": "Casos de Uso Práctico",
    "documentation": "Documentación",
    "legal": "Legal",
    "privacy_policy": "Política de Privacidad",
    "terms_of_service": "Términos de Servicio",
    "cookie_policy": "Política de Cookies",
    "gdpr_compliance": "Cumplimiento GDPR",
    "rights_reserved": "Todos los derechos reservados",
    "compliance_security": "Cumplimiento y Seguridad",
    "compliance_security_desc": "Marcos de seguridad y cumplimiento de nivel empresarial para implementaciones de IA.",
    "learn_more": "Más Información",
    "sending": "Enviando...",
    "subscribe_success": "¡Gracias por suscribirse a nuestro boletín!",
    "ai_deployment_info": "Información de Despliegue IA",
    "ai_deployment_desc": "Contáctenos para conversar sobre cómo los frameworks de IA privada pueden encajar en su organización.",
    "enterprise_automation_toast": "Automatización Empresarial",
    "enterprise_automation_desc": "Descubra cómo las metodologías de automatización gobernada pueden apoyar sus procesos empresariales complejos.",
    "well_be_in_touch": "¡Nos pondremos en contacto pronto!",
    success_stories: {
      title: "Casos de Uso Práctico",
      subtitle: "Demostraciones prácticas de nuestras soluciones de IA",
      stories: [
        {
          title: "Local AI Integration",
          description: "Demostración de soluciones de IA local para procesamiento seguro y privado de datos",
          metrics: {
            efficiency: "90%",
            accuracy: "95%",
            users: "500+",
            time: "3 meses"
          }
        },
        {
          title: "Microsoft 365 Integration",
          description: "Demostración de integración de IA local con Microsoft 365 para documentación técnica y cumplimiento",
          metrics: {
            efficiency: "85%",
            accuracy: "92%",
            users: "1000+",
            time: "4 meses"
          }
        },
        {
          title: "AI-Powered Automation",
          description: "Demostración de automatización impulsada por IA para procesos empresariales",
          metrics: {
            efficiency: "95%",
            accuracy: "98%",
            users: "2000+",
            time: "6 meses"
          }
        }
      ]
    },
    metrics: {
      efficiency: "Velocidad de Proceso",
      capacity: "Capacidad de Usuarios",
      time_saved: "Reducción de Tiempo",
      accuracy: "Precisión de Detección",
      response_time: "Tiempo de Respuesta",
      hours_week: "Horas Semanales Ahorradas",
      efficiency_improvement: "Mejora de Eficiencia",
      process_automation_rate: "Tasa de Automatización de Procesos",
      error_reduction: "Reducción de Errores",
      document_accuracy: "Precisión Documental",
      processing_speed: "Velocidad de Procesamiento",
      cost_savings: "Ahorro de Costos",
      response_accuracy: "Precisión de Respuesta",
      customer_satisfaction: "Satisfacción del Cliente",
      deployment_time: "Tiempo de Despliegue",
      result_accuracy: "Precisión de Resultados",
      data_processing_efficiency: "Eficiencia en el Procesamiento de Datos"
    }
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const storedLanguage = localStorage.getItem('language') as Language;
    return storedLanguage || (navigator.language.startsWith('es') ? 'es' : 'en');
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): any => {
    const keys = key.split('.');
    let value: any = translations[language as keyof typeof translations];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k as keyof typeof value];
      } else {
        return key;
      }
    }

    return value;
  };

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
